import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Briefcase, 
  Search, 
  MapPin, 
  Phone, 
  Mail, 
  Star,
  Building2,
  Users,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TitleCompaniesPage({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const { mainClass } = useSidebar();

  useEffect(() => {
    fetchTitleCompanies();
  }, []);

  const fetchTitleCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/title/directory`, {
        withCredentials: true
      });
      setCompanies(response.data || []);
    } catch (error) {
      console.error("Failed to fetch title companies:", error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchQuery || 
      company.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.contact_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCity = !cityFilter || 
      company.city?.toLowerCase().includes(cityFilter.toLowerCase());
    return matchesSearch && matchesCity;
  });

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass}>
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                Title Company Directory
              </h1>
              <p className="text-white/50 mt-1">Find title companies for your closings</p>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <Input
                  placeholder="Search by company name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-black border-white/[0.08] text-white"
                />
              </div>
              <div className="w-full md:w-64 relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                <Input
                  placeholder="Filter by city..."
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="pl-10 bg-black border-white/[0.08] text-white"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center gap-3">
                <Building2 className="w-8 h-8 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
                <div>
                  <p className="text-2xl font-bold text-white">{companies.length}</p>
                  <p className="text-sm text-white/50">Title Companies</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-8 h-8 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {new Set(companies.map(c => c.city).filter(Boolean)).size}
                  </p>
                  <p className="text-sm text-white/50">Cities Covered</p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">
                    {companies.reduce((sum, c) => sum + (c.orders_completed || 0), 0)}
                  </p>
                  <p className="text-sm text-white/50">Orders Completed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Companies Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
              <Briefcase className="w-16 h-16 text-white/30 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">No Title Companies Found</h3>
              <p className="text-white/50 mb-6">
                {companies.length === 0 
                  ? "Title companies will appear here once they join the platform."
                  : "No companies match your search criteria. Try adjusting your filters."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCompanies.map((company) => (
                <div 
                  key={company.company_id}
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4 hover:border-purple-500/50 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {company.logo_url ? (
                        <img src={company.logo_url} alt={company.company_name} className="w-full h-full rounded-2xl object-cover" />
                      ) : (
                        company.company_name?.charAt(0) || "T"
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate">{company.company_name}</h3>
                      <p className="text-sm text-white/50 truncate">{company.contact_name}</p>
                      {company.city && (
                        <p className="text-xs text-white/40 flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {company.city}, {company.state}
                        </p>
                      )}
                    </div>
                    {company.rating && (
                      <div className="flex items-center gap-1 text-gold">
                        <Star className="w-4 h-4 fill-gold" />
                        <span className="text-sm font-medium">{company.rating}</span>
                      </div>
                    )}
                  </div>
                  
                  {company.orders_completed > 0 && (
                    <div className="mt-3 text-xs text-white/40">
                      <CheckCircle className="w-3 h-3 inline mr-1 text-green-400" />
                      {company.orders_completed} orders completed
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-white/[0.08] flex items-center gap-2">
                    {company.phone && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 border-white/[0.1] text-white/70 hover:text-white hover:border-white/[0.15]"
                        onClick={() => window.open(`tel:${company.phone}`)}
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </Button>
                    )}
                    {company.email && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 border-white/[0.1] text-white/70 hover:text-white hover:border-white/[0.15]"
                        onClick={() => window.open(`mailto:${company.email}`)}
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </Button>
                    )}
                    <Button 
                      size="sm"
                      className="flex-1 bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 border border-purple-500/30"
                      onClick={() => navigate(`/title-companies/${company.company_id}`)}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
